import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LayoutDashboard, Camera, MapPin, BarChart3, FileCheck,
  Shield, Lock, CheckCircle2, Circle, Loader2, AlertCircle,
  Save, RefreshCw, Image as ImageIcon, Calculator, Sparkles,
} from 'lucide-react';

import VistoriaRegiaoFator01Servicos       from './VistoriaRegiaoFator01Servicos';
import VistoriaRegiaoFator02Infra          from './VistoriaRegiaoFator02Infra';
import VistoriaRegiaoFator03Seguranca      from './VistoriaRegiaoFator03Seguranca';
import VistoriaRegiaoFator04Acessibilidade from './VistoriaRegiaoFator04Acessibilidade';
import VistoriaRegiaoFator05Comercio       from './VistoriaRegiaoFator05Comercio';
import VistoriaRegiaoFator06Ocupacao       from './VistoriaRegiaoFator06Ocupacao';
import VistoriaInterna_TabelaAreas         from './VistoriaInterna_TabelaAreas';
import VistoriaInterna_AuditorAtivos       from './VistoriaInterna_AuditorAtivos';
import VistoriaFotos                       from './VistoriaFotos';
import VistoriaEspacial_MapaVoz            from './VistoriaEspacial_MapaVoz';
import UploadPlanta                        from './UploadPlanta';
import ResumoIndiceProvas                  from './ResumoIndiceProvas';
import VistoriaRegiaoResumo                from './VistoriaRegiaoResumo';

import { ETAPA, ORDEM_FATORES, FATORES_CONFIG, listarFatores } from '../../constants/vistoria';
import { laudoApi } from '../../services/laudoApi';
import { AuditoriaNormativaService } from '../../services/AuditoriaNormativaService';

/**
 * ============================================================
 * COMPONENTE: VistoriaRegiaoMain
 * ------------------------------------------------------------
 * Orquestrador do módulo de vistoria regional.
 *
 *   ✔ Roteia os 6 fatores via ETAPA (sem strings mágicas)
 *   ✔ F06 Ocupação inclui módulos internos:
 *       - Tabela de Áreas Equivalente (NBR 12.721)
 *       - Auditor de Ativos (fator de homogeneização)
 *   ✔ Sidebar com progresso real
 *   ✔ Estado central compartilhado entre etapas
 *   ✔ Upload de planta + pins do mapa + fotos gerais
 *   ✔ Autosave com debounce (30s)
 * VERSÃO: 2.3.0
 * ============================================================
 */

/* =========================
   MAPA DE COMPONENTES POR FATOR
========================= */

const COMPONENTES_FATOR = {
  [ETAPA.F01_SERVICOS]:       VistoriaRegiaoFator01Servicos,
  [ETAPA.F02_INFRA]:          VistoriaRegiaoFator02Infra,
  [ETAPA.F03_SEGURANCA]:      VistoriaRegiaoFator03Seguranca,
  [ETAPA.F04_ACESSIBILIDADE]: VistoriaRegiaoFator04Acessibilidade,
  [ETAPA.F05_COMERCIO]:       VistoriaRegiaoFator05Comercio,
  // F06 é tratado separadamente por ter módulos internos
};

/* =========================
   COMPONENTE
========================= */

export default function VistoriaRegiaoMain({
  infoLaudo = { numero: 'REPORT-2026-MH-001', engenheiro: 'MARCUS FERRAZ' },
  peritoAtivo = 'MONTE_HERMOM_USER',
  laudoInicial = {},
  onProtocolo,
  autosaveMs = 30000,
} = {}) {

  /* =========================
     ESTADO CENTRAL
  ========================= */

  const [laudo, setLaudo] = useState(() => ({
    // Estruturas de persistência entre sessões
    pinsMapa: [],
    fotosGerais: [],
    plantaImg: null,
    plantaMeta: null,

    // Módulos internos do F06
    areasInternas: null,
    tipologiaAtivos: 'MEDIO',
    ativosSelecionados: {},
    fatorAtivos: null,

    ...laudoInicial,
  }));

  const [etapaAtiva, setEtapaAtiva] = useState(ETAPA.F01_SERVICOS);
  const [sincronizando, setSincronizando] = useState(false);
  const [statusFila, setStatusFila] = useState(null);
  const [autosaveStatus, setAutosaveStatus] = useState(null);
  const [erro, setErro] = useState(null);

  /* =========================
     HELPERS DE ESTADO
  ========================= */

  const atualizarLaudo = useCallback((chave, valor) => {
    setLaudo(prev => ({
      ...prev,
      [chave]: typeof valor === 'function' ? valor(prev[chave]) : valor,
    }));
  }, []);

  const mesclarLaudo = useCallback((patch) => {
    setLaudo(prev => ({ ...prev, ...patch }));
  }, []);

  /* =========================
     PLANTA
  ========================= */

  const handleUploadPlanta = useCallback(({ plantaImg, plantaMeta }) => {
    setLaudo(prev => ({ ...prev, plantaImg, plantaMeta }));
  }, []);

  const handleRemoverPlanta = useCallback(() => {
    setLaudo(prev => ({ ...prev, plantaImg: null, plantaMeta: null }));
  }, []);

  /* =========================
     PINS DO MAPA
  ========================= */

  const pinsMapa = laudo.pinsMapa || [];

  const handleChangePins = useCallback((novosPins) => {
    setLaudo(prev => ({
      ...prev,
      pinsMapa: Array.isArray(novosPins) ? novosPins : [],
    }));
  }, []);

  const handleNovoPin = useCallback((pin) => {
    setLaudo(prev => ({
      ...prev,
      pinsMapa: [...(prev.pinsMapa || []), pin],
    }));
  }, []);

  const handleRemoverPin = useCallback((pinId) => {
    setLaudo(prev => ({
      ...prev,
      pinsMapa: (prev.pinsMapa || []).filter(p => p.id !== pinId),
    }));
  }, []);

  const handleLimparPins = useCallback(() => {
    setLaudo(prev => ({ ...prev, pinsMapa: [] }));
  }, []);

  /* =========================
     FOTOS GERAIS
  ========================= */

  const fotosGerais = laudo.fotosGerais || [];

  const handleChangeFotos = useCallback((novasFotos) => {
    setLaudo(prev => ({
      ...prev,
      fotosGerais: Array.isArray(novasFotos) ? novasFotos : [],
    }));
  }, []);

  /* =========================
     MÓDULOS INTERNOS DO F06
  ========================= */

  const handleAreasChange = useCallback((areas) => {
    setLaudo(prev => ({ ...prev, areasInternas: areas }));
  }, []);

  const handleAreasPersistido = useCallback((areas) => {
    setLaudo(prev => ({ ...prev, areasInternas: areas }));
  }, []);

  const handleAtivosChange = useCallback(({ tipologia, selecionados, fatorFinal }) => {
    setLaudo(prev => ({
      ...prev,
      tipologiaAtivos: tipologia,
      ativosSelecionados: selecionados,
      fatorAtivos: fatorFinal,
    }));
  }, []);

  const handleAtivosPersistido = useCallback(({ tipologia, selecionados, fatorFinal }) => {
    setLaudo(prev => ({
      ...prev,
      tipologiaAtivos: tipologia,
      ativosSelecionados: selecionados,
      fatorAtivos: fatorFinal,
    }));
  }, []);

  /* =========================
     SINCRONIZA FILA OFFLINE
  ========================= */

  useEffect(() => {
    let cancelado = false;

    (async () => {
      try {
        setSincronizando(true);
        const resultado = await laudoApi.sincronizarFila();
        if (cancelado) return;
        if (resultado.enviados > 0 || resultado.falhas > 0) {
          setStatusFila(resultado);
        }
      } catch {
        /* silencioso */
      } finally {
        if (!cancelado) setSincronizando(false);
      }
    })();

    return () => { cancelado = true; };
  }, []);

  /* =========================
     NAVEGAÇÃO TIPADA
  ========================= */

  const irPara = useCallback((etapa) => {
    if (etapa === etapaAtiva) return;
    setEtapaAtiva(etapa);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [etapaAtiva]);

  /* =========================
     AUTOSAVE
  ========================= */

  useEffect(() => {
    if (!autosaveMs || autosaveMs <= 0) return;
    if (!infoLaudo?.numero) return;

    const snapshot = {
      pinsMapa: laudo.pinsMapa || [],
      fotosGerais: laudo.fotosGerais || [],
      plantaImg: laudo.plantaImg || null,
      plantaMeta: laudo.plantaMeta || null,
      areasInternas: laudo.areasInternas || null,
      tipologiaAtivos: laudo.tipologiaAtivos || null,
      ativosSelecionados: laudo.ativosSelecionados || {},
      fatorAtivos: laudo.fatorAtivos || null,
      etapaAtiva,
    };

    const timer = setTimeout(async () => {
      setAutosaveStatus('salvando');
      try {
        await laudoApi.salvarModulo({
          report_id: infoLaudo.numero,
          modulo: 'VISTORIA_AUTOSAVE',
          perito: peritoAtivo,
          timestamp: new Date().toISOString(),
          ...snapshot,
        });
        setAutosaveStatus('salvo');
        setTimeout(() => setAutosaveStatus(null), 3000);
      } catch {
        setAutosaveStatus('erro');
        setTimeout(() => setAutosaveStatus(null), 5000);
      }
    }, autosaveMs);

    return () => clearTimeout(timer);
  }, [
    laudo.pinsMapa,
    laudo.fotosGerais,
    laudo.plantaImg,
    laudo.plantaMeta,
    laudo.areasInternas,
    laudo.tipologiaAtivos,
    laudo.ativosSelecionados,
    laudo.fatorAtivos,
    etapaAtiva,
    autosaveMs,
    infoLaudo?.numero,
    peritoAtivo,
  ]);

  /* =========================
     STATUS DE CADA ETAPA
  ========================= */

  const statusEtapas = useMemo(() => {
    const mapa = {};
    for (const etapa of ORDEM_FATORES) {
      const dadosFator = laudo[etapa];
      if (!dadosFator) {
        mapa[etapa] = 'VAZIO';
      } else if (dadosFator.statusRevisao === 'REVISADO') {
        mapa[etapa] = 'REVISADO';
      } else {
        mapa[etapa] = 'PREENCHIDO';
      }
    }
    return mapa;
  }, [laudo]);

  /* =========================
     RESUMO GLOBAL
  ========================= */

  const resumo = useMemo(
    () => AuditoriaNormativaService.auditarLaudoCompleto(laudo),
    [laudo]
  );

  /* =========================
     RENDER DO CONTEÚDO
  ========================= */

  const renderConteudo = () => {

    /* ---------- F06 Ocupação — MÓDULOS INTERNOS (exceção) ---------- */
    if (etapaAtiva === ETAPA.F06_OCUPACAO) {
      return (
        <div className="space-y-8">
          {/* 1. Fator regional padrão */}
          <VistoriaRegiaoFator06Ocupacao
            setNavegacao={setEtapaAtiva}
            infoLaudo={infoLaudo}
            peritoAtivo={peritoAtivo}
          />

          {/* 2. Tabela de Áreas Equivalente (NBR 12.721) */}
          <section className="space-y-3">
            <div className="flex items-center gap-3 px-2">
              <Calculator size={18} className="text-amber-400" />
              <h3 className="text-[11px] text-slate-500 tracking-[0.4em] font-black uppercase">
                Módulo Interno · Áreas Equivalentes
              </h3>
            </div>
            <VistoriaInterna_TabelaAreas
              reportNumber={infoLaudo.numero}
              peritoAtivo={peritoAtivo}
              areas={laudo.areasInternas || undefined}
              onChange={handleAreasChange}
              onPersistido={handleAreasPersistido}
              infoLaudo={infoLaudo}
            />
          </section>

          {/* 3. Auditor de Ativos Construtivos */}
          <section className="space-y-3">
            <div className="flex items-center gap-3 px-2">
              <Sparkles size={18} className="text-amber-400" />
              <h3 className="text-[11px] text-slate-500 tracking-[0.4em] font-black uppercase">
                Módulo Interno · Auditoria de Ativos
              </h3>
            </div>
            <VistoriaInterna_AuditorAtivos
              reportNumber={infoLaudo.numero}
              peritoAtivo={peritoAtivo}
              tipologia={laudo.tipologiaAtivos || 'MEDIO'}
              selecionados={laudo.ativosSelecionados || {}}
              onChange={handleAtivosChange}
              onPersistido={handleAtivosPersistido}
            />
          </section>
        </div>
      );
    }

    /* ---------- F01–F05: fatores regionais genéricos ---------- */
    const ComponenteFator = COMPONENTES_FATOR[etapaAtiva];
    if (ComponenteFator) {
      return (
        <ComponenteFator
          setNavegacao={setEtapaAtiva}
          infoLaudo={infoLaudo}
          peritoAtivo={peritoAtivo}
        />
      );
    }

    /* ---------- Evidências fotográficas ---------- */
    if (etapaAtiva === ETAPA.FOTOS) {
      return (
        <VistoriaFotos
          laudo={laudo}
          setLaudo={setLaudo}
          setNavegacao={setEtapaAtiva}
          peritoAtivo={peritoAtivo}
          infoLaudo={infoLaudo}
          fotosData={fotosGerais}
          setFotosData={handleChangeFotos}
        />
      );
    }

    /* ---------- Mapa / Voz ---------- */
    if (etapaAtiva === ETAPA.MAPA) {
      return (
        <div className="space-y-6">
          <UploadPlanta
            plantaImg={laudo.plantaImg || null}
            plantaMeta={laudo.plantaMeta || null}
            reportNumber={infoLaudo.numero}
            onChange={handleUploadPlanta}
            onRemover={handleRemoverPlanta}
          />

          {laudo.plantaImg ? (
            <VistoriaEspacial_MapaVoz
              plantaImg={laudo.plantaImg}
              reportNumber={infoLaudo.numero}
              fatorId={ETAPA.MAPA}
              pins={pinsMapa}
              onChangePins={handleChangePins}
              onNovoPin={handleNovoPin}
            />
          ) : (
            <div className="bg-white/[0.02] border-2 border-dashed border-white/10 rounded-[2rem] p-16 text-center">
              <p className="text-slate-500 text-[11px] tracking-widest">
                CARREGUE A PLANTA ACIMA PARA MAPEAR AS EVIDÊNCIAS
              </p>
            </div>
          )}
        </div>
      );
    }

    /* ---------- Resumo / Protocolo ---------- */
    if (etapaAtiva === ETAPA.RESUMO) {
      return (
        <VistoriaRegiaoResumo
          laudo={laudo}
          setLaudo={setLaudo}
          setNavegacao={setEtapaAtiva}
          infoLaudo={infoLaudo}
          onProtocolo={onProtocolo}
        />
      );
    }

    /* ---------- Índices NBR ---------- */
    if (etapaAtiva === ETAPA.PROTOCOLO) {
      return <ResumoIndiceProvas laudo={laudo} statusGeral={resumo} />;
    }

    return null;
  };

  /* =========================
     TÍTULO DINÂMICO
  ========================= */

  const tituloEtapa = useMemo(() => {
    if (etapaAtiva.startsWith('f0')) {
      return FATORES_CONFIG[etapaAtiva]?.titulo || etapaAtiva.toUpperCase();
    }
    if (etapaAtiva === ETAPA.FOTOS)      return 'EVIDÊNCIAS';
    if (etapaAtiva === ETAPA.MAPA)       return 'GEOLOCALIZAÇÃO';
    if (etapaAtiva === ETAPA.RESUMO)     return 'RESUMO / PROTOCOLO';
    if (etapaAtiva === ETAPA.PROTOCOLO)  return 'ÍNDICES NBR';
    return etapaAtiva.toUpperCase();
  }, [etapaAtiva]);

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="flex h-screen bg-[#F8FAFC] font-sans overflow-hidden">

      {/* =========================
          SIDEBAR
      ========================= */}
      <aside className="w-80 bg-[#2F353B] p-8 flex flex-col justify-between shadow-2xl z-20 overflow-y-auto">

        <div>
          <div className="mb-10 border-b border-slate-600 pb-6">
            <h1 className="text-white font-black italic text-[22px] tracking-tighter">
              MONTE HERMOM
            </h1>
            <span className="text-blue-400 text-[10px] font-bold uppercase tracking-widest">
              Digital Asset Management
            </span>
          </div>

          {/* FATORES */}
          <nav className="space-y-2 mb-8">
            <p className="text-slate-500 text-[9px] font-black uppercase tracking-[0.3em] mb-4 px-3">
              Fatores NBR 14653
            </p>
            {listarFatores().map(fator => {
              const status = statusEtapas[fator.etapa];
              const ativo = etapaAtiva === fator.etapa;

              return (
                <button
                  key={fator.etapa}
                  type="button"
                  onClick={() => irPara(fator.etapa)}
                  className={`w-full flex items-center justify-between gap-3 p-4 rounded-2xl font-black text-[10px] uppercase italic transition-all ${
                    ativo
                      ? 'bg-blue-600 text-white shadow-lg translate-x-1'
                      : status === 'REVISADO'
                        ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                        : status === 'PREENCHIDO'
                          ? 'text-slate-200 hover:bg-slate-700'
                          : 'text-slate-500 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <StatusIcon status={status} ativo={ativo} />
                    <span className="truncate">
                      {fator.codigo} · {fator.titulo.split('. ')[1]}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>

          {/* MÓDULOS EXTRAS */}
          <nav className="space-y-2">
            <p className="text-slate-500 text-[9px] font-black uppercase tracking-[0.3em] mb-4 px-3">
              Módulos Complementares
            </p>
            {[
              { id: ETAPA.FOTOS, label: 'Evidências (Lupa)', icon: Camera, badge: fotosGerais.length },
              { id: ETAPA.MAPA, label: 'Geolocalização', icon: MapPin, badge: pinsMapa.length },
              { id: ETAPA.RESUMO, label: 'Resumo / Protocolo', icon: FileCheck, badge: null },
            ].map(item => {
              const ativo = etapaAtiva === item.id;
              const Icone = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => irPara(item.id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl font-black text-[10px] uppercase italic transition-all ${
                    ativo
                      ? 'bg-blue-600 text-white shadow-lg translate-x-1'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <Icone size={14} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge > 0 && (
                    <span className={`text-[9px] px-2 py-0.5 rounded-full ${
                      ativo ? 'bg-white/20 text-white' : 'bg-white/10 text-slate-300'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* RODAPÉ */}
        <div className="space-y-3 pt-6">
          <div className="bg-slate-800/50 p-5 rounded-3xl border border-slate-600">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-400 text-[9px] font-black uppercase tracking-widest">
                Progresso
              </span>
              <span className={`text-[11px] font-black ${
                resumo.podeProtocolar ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {resumo.revisados} / {resumo.totalFatores}
              </span>
            </div>
            <div className="w-full h-1.5 bg-slate-900 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  resumo.podeProtocolar ? 'bg-emerald-500' : 'bg-amber-500'
                }`}
                style={{ width: `${(resumo.revisados / resumo.totalFatores) * 100}%` }}
              />
            </div>
            {!resumo.podeProtocolar && resumo.bloqueados.length > 0 && (
              <p className="text-amber-500 text-[9px] font-black uppercase mt-3 flex items-center gap-2">
                <AlertCircle size={10} />
                {resumo.bloqueados.length} fator(es) bloqueado(s)
              </p>
            )}
          </div>

          {autosaveStatus && (
            <div className={`p-3 rounded-2xl border flex items-center gap-2 ${
              autosaveStatus === 'salvando'
                ? 'bg-blue-500/10 border-blue-500/20 text-blue-400'
                : autosaveStatus === 'salvo'
                  ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                  : 'bg-red-500/10 border-red-500/20 text-red-400'
            }`}>
              {autosaveStatus === 'salvando' && <Loader2 size={10} className="animate-spin" />}
              {autosaveStatus === 'salvo' && <CheckCircle2 size={10} />}
              {autosaveStatus === 'erro' && <AlertCircle size={10} />}
              <span className="text-[9px] font-black uppercase tracking-widest">
                {autosaveStatus === 'salvando'
                  ? 'Salvando...'
                  : autosaveStatus === 'salvo'
                    ? 'Salvo automaticamente'
                    : 'Falha no autosave'}
              </span>
            </div>
          )}

          {(sincronizando || statusFila) && (
            <div className={`p-4 rounded-2xl border flex items-center gap-3 ${
              sincronizando
                ? 'bg-blue-500/10 border-blue-500/20'
                : statusFila?.falhas > 0
                  ? 'bg-amber-500/10 border-amber-500/20'
                  : 'bg-emerald-500/10 border-emerald-500/20'
            }`}>
              {sincronizando ? (
                <>
                  <Loader2 size={12} className="text-blue-400 animate-spin" />
                  <span className="text-blue-400 text-[9px] font-black uppercase">
                    Sincronizando fila...
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  <span className="text-emerald-400 text-[9px] font-black uppercase">
                    {statusFila.enviados} enviado(s)
                    {statusFila.falhas > 0 ? ` · ${statusFila.falhas} pendente(s)` : ''}
                  </span>
                </>
              )}
            </div>
          )}

          <div className="bg-slate-800/50 p-4 rounded-2xl border border-slate-600 text-white text-[10px] font-black uppercase flex items-center gap-2">
            <Shield size={14} className="text-blue-400 shrink-0" />
            <span className="truncate">{infoLaudo.numero}</span>
          </div>
        </div>
      </aside>

      {/* =========================
          ÁREA DE EXECUÇÃO
      ========================= */}
      <main className="flex-1 overflow-y-auto bg-[#F1F5F9] relative">

        {/* BREADCRUMB */}
        <div className="sticky top-0 z-10 bg-[#F1F5F9]/95 backdrop-blur-sm border-b border-slate-200 px-12 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-6">
            <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest">
              <span className="text-slate-400">{infoLaudo.numero}</span>
              <span className="text-slate-300">/</span>
              <span className="text-[#2F353B]">{tituloEtapa}</span>
            </div>

            <div className="flex items-center gap-3">
              {laudo.plantaImg && (
                <span className="bg-emerald-500/10 text-emerald-600 text-[9px] font-black uppercase tracking-widest px-3 py-2 rounded-full flex items-center gap-2">
                  <ImageIcon size={10} />
                  Planta
                </span>
              )}

              {pinsMapa.length > 0 && (
                <span className="bg-amber-500/10 text-amber-600 text-[9px] font-black uppercase tracking-widest px-3 py-2 rounded-full flex items-center gap-2">
                  <MapPin size={10} />
                  {pinsMapa.length} pin(s)
                </span>
              )}

              {fotosGerais.length > 0 && (
                <span className="bg-blue-500/10 text-blue-600 text-[9px] font-black uppercase tracking-widest px-3 py-2 rounded-full flex items-center gap-2">
                  <Camera size={10} />
                  {fotosGerais.length} foto(s)
                </span>
              )}

              {resumo.podeProtocolar ? (
                <span className="bg-emerald-500/10 text-emerald-600 text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full flex items-center gap-2">
                  <CheckCircle2 size={12} />
                  Pronto para protocolar
                </span>
              ) : (
                <span className="bg-amber-500/10 text-amber-600 text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full flex items-center gap-2">
                  <Lock size={12} />
                  {6 - resumo.revisados} fator(es) pendente(s)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ERRO GLOBAL */}
        {erro && (
          <div className="max-w-7xl mx-auto px-12 pt-6">
            <div className="bg-red-50 border-2 border-red-500/30 rounded-2xl p-5 flex items-start gap-4">
              <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
              <span className="text-red-700 text-[11px] tracking-wide leading-relaxed normal-case flex-1">
                {erro}
              </span>
            </div>
          </div>
        )}

        {/* CONTEÚDO */}
        <div className="p-12 max-w-7xl mx-auto">
          {renderConteudo()}
        </div>
      </main>
    </div>
  );
}

/* =========================
   SUB-COMPONENTE
========================= */

function StatusIcon({ status, ativo }) {
  if (ativo) return <Loader2 size={14} className="animate-spin" />;
  if (status === 'REVISADO') return <CheckCircle2 size={14} />;
  if (status === 'PREENCHIDO') return <CheckCircle2 size={14} className="opacity-50" />;
  return <Circle size={14} />;
}